import { useContext, useMemo } from 'react';
import { IntlContext } from 'react-intl';

import { createAxisTickFormatter, resolveChartLocale } from 'components/charts/utils/format-axis-tick';
import { useOptionalLocaleState } from 'contexts/locale/locale-context';
import { En } from 'translations/en';
import { ChartLocalization } from 'components/charts/types/chart-localization.type';
import { ChartMessages } from 'types/language/charts.lang';

const IMAGE_TYPE_LABELS: Record<string, string> = { 'image/png': 'PNG', 'image/jpeg': 'JPEG', 'image/webp': 'WebP' };

// Numbers follow the user's number-format locale; messages follow the app language.
export const useChartLocale = (): ChartLocalization => {
  const intl = useContext(IntlContext);
  const numberLocale = useOptionalLocaleState()?.locale ?? intl?.locale;

  return useMemo(() => {
    const locale = resolveChartLocale(numberLocale);
    const numberFormatter = new Intl.NumberFormat(locale);
    const message = (key: keyof ChartMessages, values?: { type: string }) =>
      intl
        ? intl.formatMessage({ id: `charts.${key}`, defaultMessage: En.charts[key] }, values)
        : En.charts[key].replace('{type}', values?.type ?? '');

    return {
      locale,
      formatValue: (value) => (value === null ? '' : numberFormatter.format(value)),
      axisValueFormatter: createAxisTickFormatter(locale),
      localeText: {
        loading: message('loading'),
        noData: message('noData'),
        a11yNoValue: message('a11yNoValue'),
        toolbarExport: message('toolbarExport'),
        toolbarExportPrint: message('toolbarExportPrint'),
        toolbarExportImage: (mimeType) => message('toolbarExportImage', { type: IMAGE_TYPE_LABELS[mimeType] ?? mimeType }),
      },
    };
  }, [intl, numberLocale]);
};
